import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { useState, useCallback, FormEvent } from "react";
import { useCreateAvailabilityMutation } from "src/api/services/availabilityApi";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "src/components/Buttons/LoadingButton";
import ProviderSelect from "src/components/ProviderSelect";
import { ProviderProfile } from "src/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateAvailabilityModal = ({ open, onClose }: Props) => {
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [provider, setProvider] = useState<ProviderProfile | null>(null);
  const [createAvailability, { isLoading }] = useCreateAvailabilityMutation();

  const handleProvider = useCallback((value: ProviderProfile | null) => {
    setProvider(value);
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (provider?.id != null) {
        createAvailability({
          provider_id: Number(provider.id),
          date,
          windows: [{ start_time: startTime, end_time: endTime }],
        })
          .unwrap()
          .then(() => {
            setDate("");
            setStartTime("");
            setEndTime("");
            onClose();
          });
      }
    },
    [createAvailability, provider?.id, date, startTime, endTime, onClose],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Availability
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        <ProviderSelect
          required
          value={provider}
          onChange={(v) => handleProvider(v)}
          select={(v) => v}
          multiple={false}
        />

        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <TextField
          label="Start Time"
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <TextField
          label="End Time"
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <Stack direction="row" gap={2} sx={{ marginTop: "auto" }}>
          <Button fullWidth variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton fullWidth loading={isLoading} type="submit">
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateAvailabilityModal;
